import { useTranslation } from 'react-i18next';
import { formatDateByDayjs } from '@/utils';
import {
  NOT_RETURNED_ROW,
  RETURN_LINE,
  boxReturnState,
  isReturnLeg,
  returnTally,
  type ReturnBox,
} from '@/pages/order-details/components/boxReturnState';

interface RtoBox extends ReturnBox {
  sequence: number;
  size: string;
}

interface RtoOrderBoxesProps {
  boxes?: RtoBox[];
  statusSlug?: string;
}

/**
 * BOXY-556 — under a multi-box order on the RTO screen: one line per box with
 * when it came back, red for a box that should be back and is not.
 */
export function RtoOrderBoxes({ boxes, statusSlug }: RtoOrderBoxesProps) {
  const { t } = useTranslation();
  if (!boxes || boxes.length < 2 || !isReturnLeg(statusSlug)) return null;

  const count = boxes.length;
  const { returned, count: returnable } = returnTally(boxes);

  return (
    <div className='mx-5 mb-3 rounded-lg ring-1 ring-inset ring-sd-input'>
      <p className='m-0 px-3 pt-2 text-[11px] font-semibold text-[rgb(115,123,140)]'>
        {String(t('boxes_returned_of', { received: returned, count: returnable }))}
      </p>
      <ul className='m-0 list-none divide-y divide-sd-input ps-0'>
        {boxes.map((box) => {
          const state = boxReturnState(box, statusSlug) ?? 'awaiting';
          const { Icon, labelKey, tone, at } = RETURN_LINE[state];
          const time = at ? box[at] : null;
          return (
            <li
              key={box.sequence}
              data-state={state}
              className={`flex items-center gap-3 px-3 py-2 text-[12px] ${state === 'not_returned' ? NOT_RETURNED_ROW : ''}`}
            >
              <span className='shrink-0 font-medium tabular-nums' dir='ltr'>
                {`BOX ${box.sequence}/${count}`}
              </span>
              <span className='w-8 shrink-0 rounded border border-sd-border bg-sd-muted text-center text-[11px] font-medium'>
                {box.size}
              </span>
              <span className={`flex min-w-0 flex-1 items-center gap-1.5 ${tone}`}>
                <Icon className='h-3.5 w-3.5 shrink-0' />
                <span className='min-w-0 break-words'>{String(t(labelKey))}</span>
              </span>
              <span className='shrink-0 tabular-nums text-sd-muted-foreground' dir='ltr'>
                {time ? formatDateByDayjs(time, 'DD MMM, HH:mm') : '—'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
